import { motion } from 'motion/react';
import { Container, Kicker, Mark } from './primitives';

const PHASES = [
  {
    code: '01',
    name: 'Cantiere',
    title: 'Cattura, nella lingua del campo.',
    copy: 'Il caposquadra apre BOSS sul telefono e compila il rapportino: chi c\'era, quante ore, quali mezzi, quali materiali, due foto. Si salva da solo mentre scrive. Niente fogli di carta, niente foto su WhatsApp.',
    chip: '17:42 · rapportino · 4 persone · 32 h',
  },
  {
    code: '02',
    name: 'Sala operativa',
    title: 'Componi, nella lingua dell\'ufficio.',
    copy: 'Il segnale dal campo arriva già leggibile: i DDT dei fornitori letti dall\'AI, le ore riconciliate riga per riga. Il rapportino si chiude in minuti, non in un pomeriggio, e confluisce nel quadro economico della commessa.',
    chip: '17:43 · DDT letto · ore riconciliate',
  },
  {
    code: '03',
    name: 'Direzione',
    title: 'Decidi, con il quadro completo.',
    copy: 'La direzione vede oltre settanta cantieri in un solo schermo: avanzamento, margine, compilazioni e chiusure. E a fine mese le ore sono già pronte per il consulente del lavoro.',
    chip: '08:15 · 70 cantieri · margine aggiornato',
  },
];

export function Workflow({ index = '04' }: { index?: string }) {
  return (
    <section id="workflow" className="relative border-t border-border py-28 md:py-36">
      <Container>
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-[380px_1fr] lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:sticky lg:top-28 lg:self-start"
          >
            <Kicker index={index} label="Come funziona" />
            <h2 className="boss-display mt-5 text-balance text-[44px] md:text-[56px]">
              Dal <Mark>ponteggio</Mark> <br />alla direzione, <br />in un solo filo.
            </h2>
            <p className="mt-5 text-pretty text-[15px] leading-[1.55] text-fg-2">
              L'edilizia è sempre stata un problema di traduzione. Il capocantiere parla al geometra, il
              geometra parla alla direzione, la direzione parla al cliente — a ogni traduzione si perde
              qualcosa. BOSS toglie le traduzioni.
            </p>
          </motion.div>

          <ol className="relative space-y-12">
            <span className="absolute left-[15px] top-2 bottom-2 hidden w-px bg-border md:block" />
            {PHASES.map((p, i) => (
              <motion.li
                key={p.code}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative md:pl-14"
              >
                <span className="absolute left-0 top-1 hidden h-8 w-8 items-center justify-center rounded-full border border-input bg-background font-mono text-[10px] text-primary md:inline-flex">
                  {p.code}
                </span>
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground md:hidden">
                    {p.code} ·
                  </span>
                  <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-fg-2">
                    {p.name}
                  </h3>
                </div>
                <p className="boss-h1 mt-3 text-balance text-[30px] md:text-[40px]">{p.title}</p>
                <p className="mt-3 max-w-[560px] text-pretty text-[15px] leading-[1.55] text-fg-2">
                  {p.copy}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  {p.chip}
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </Container>
    </section>
  );
}
